/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import TicketInventoryService from "../../services/TicketInventoryService";
import AssetService from "../../services/AssetService";
import FacilityService from "../../services/FacilityService";
import UserService from "../../services/UserService";
import { If } from "../../utils/Controls";
import { AntdPaging, AntdSelect, AntdDatepicker } from "../../utils/Antd";
import util from "../../utils/util";
import moment from "moment";
import plus from "../assets/icons/plus.svg";
import edit from "../assets/icons/pen-to-square-solid.svg";
import search from "../assets/icons/magnifying-glass-solid.svg";
import downloa from "../assets/icons/download-solid.svg"
import {saveAs} from 'file-saver';

import excel from "../assets/icons/excel.png"; 
import acrobat from "../assets/icons/acrobat.png";

import UnallocatedReoprt from '../../utils/UnallocatedReoprt';
import {
    Input,
    Button,
    message,
    Modal,
    Card,
    Row,
    Col,
    Statistic,
    Progress,
    Badge,
} from 'antd';

// Analytics Component
const TicketAnalytics = ({ data, isNational }) => {
    const [analytics, setAnalytics] = useState({
        totalTickets: 0,
        openTickets: 0,
        onHoldTickets: 0,
        completedTickets: 0,
        closedTickets: 0,
        averageDaysOpen: 0,
        criticalTickets: 0, // tickets open > 30 days
        medEquipmentTickets: 0,
        infrastructureTickets: 0,
        facilitiesWithTickets: 0
    });

    useEffect(() => {
        if (data && data.length > 0) {
            calculateAnalytics(data);
        }
    }, [data]);

    const calculateAnalytics = (ticketData) => {
        const total = ticketData.length;
        
        // Status counts
        const statusCounts = ticketData.reduce((acc, ticket) => {
            acc[ticket.status_name] = (acc[ticket.status_name] || 0) + 1;
            return acc;
        }, {});

        // Type counts
        const typeCounts = ticketData.reduce((acc, ticket) => {
            acc[ticket.type] = (acc[ticket.type] || 0) + 1;
            return acc;
        }, {});

        // Average days open
        const totalDays = ticketData.reduce((sum, ticket) => sum + (parseInt(ticket.days_open) || 0), 0);
        const avgDays = total > 0 ? Math.round(totalDays / total) : 0;

        // Critical tickets (open > 30 days)
        const critical = ticketData.filter(ticket => parseInt(ticket.days_open) > 30).length;

        // Unique facilities
        const uniqueFacilities = isNational ? new Set(ticketData.map(ticket => ticket.client)).size : 1;

        setAnalytics({
            totalTickets: total,
            openTickets: statusCounts['Open'] || 0,
            onHoldTickets: (statusCounts['On Hold - Awaiting Spares'] || 0) + (statusCounts['On Hold - Spares Supplied'] || 0),
            completedTickets: statusCounts['Completed'] || 0,
            closedTickets: statusCounts['Closed'] || 0,
            averageDaysOpen: avgDays,
            criticalTickets: critical,
            medEquipmentTickets: typeCounts['Med/NonMed'] || 0,
            infrastructureTickets: typeCounts['Infrastructure'] || 0,
            facilitiesWithTickets: uniqueFacilities
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return '#ff4d4f';
            case 'onHold': return '#faad14';
            case 'completed': return '#52c41a';
            case 'closed': return '#722ed1';
            default: return '#1890ff';
        }
    };

    const getCriticalityLevel = () => {
        if (analytics.criticalTickets === 0) return { color: '#52c41a', text: 'Good' };
        if (analytics.criticalTickets <= 5) return { color: '#faad14', text: 'Moderate' };
        return { color: '#ff4d4f', text: 'Critical' };
    };

    const criticalityLevel = getCriticalityLevel();

    return (
        <div className="analytics-section mb-4">
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Total Tickets"
                            value={analytics.totalTickets}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Open Tickets"
                            value={analytics.openTickets}
                            valueStyle={{ color: getStatusColor('open') }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="On Hold"
                            value={analytics.onHoldTickets}
                            valueStyle={{ color: getStatusColor('onHold') }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Avg Days Open"
                            value={analytics.averageDaysOpen}
                            suffix="days"
                            valueStyle={{ color: analytics.averageDaysOpen > 15 ? '#ff4d4f' : '#52c41a' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <div>
                            <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '4px' }}>
                                Critical Tickets (More than 30 days)
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '24px', fontWeight: 'bold', color: criticalityLevel.color }}>
                                    {analytics.criticalTickets}
                                </span>
                                <Badge color={criticalityLevel.color} text={criticalityLevel.text} />
                            </div>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Med/NonMed"
                            value={analytics.medEquipmentTickets}
                            valueStyle={{ color: '#722ed1' }}
                        />
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#8c8c8c' }}>
                            Infrastructure: {analytics.infrastructureTickets}
                        </div>
                    </Card>
                </Col>
            </Row>

            {isNational === 1 && (
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                    <Col xs={24} sm={12} md={8}>
                        <Card size="small" title="Facility Coverage">
                            <Statistic
                                title="Facilities with Open Tickets"
                                value={analytics.facilitiesWithTickets}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8}>
                        <Card size="small" title="Ticket Resolution">
                            <Progress
                                percent={Math.round((analytics.completedTickets + analytics.closedTickets) / analytics.totalTickets * 100)}
                                status={analytics.criticalTickets > 0 ? "exception" : "success"}
                                strokeColor={analytics.criticalTickets > 0 ? '#ff4d4f' : '#52c41a'}
                            />
                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                                {analytics.completedTickets + analytics.closedTickets} of {analytics.totalTickets} resolved
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Card size="small" title="Performance Indicator">
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ 
                                    fontSize: '18px', 
                                    fontWeight: 'bold', 
                                    color: analytics.averageDaysOpen <= 7 ? '#52c41a' : 
                                           analytics.averageDaysOpen <= 15 ? '#faad14' : '#ff4d4f'
                                }}>
                                    {analytics.averageDaysOpen <= 7 ? 'Excellent' : 
                                     analytics.averageDaysOpen <= 15 ? 'Good' : 'Needs Attention'}
                                </div>
                                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                    Response Time Performance
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default function UnTicket() {
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [infrastuctureSubCats, setInfrastuctureSubCats] = useState([]);
    const [assets, setAssets] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [result, setResult] = useState({ data: [], page: {} });
    const [showAnalytics, setShowAnalytics] = useState(true);
    const [analyticsData, setAnalyticsData] = useState([]);
    const sdataRef = useRef({ p: 1, ps: 25 });
    const formRef = useRef();
    const dtlRef = useRef({});

    const isNational = util.isNational();
    const modules = util.getModules();
    const ticketStatusNames = [
        { id: 1, name: 'Open' },
        { id: 2, name: 'On Hold - Awaiting Spares' },
        { id: 3, name: 'On Hold - Spares Supplied' },
        { id: 4, name: 'Completed' },
        { id: 5, name: 'Closed' }
    ];

    const list3 = (p, ps, getAssignedToUsers, loadAnalytics = false) => {
        const ids = new URLSearchParams(window.location.search).get('ids');
        sdataRef.current.p = p || 1;
        sdataRef.current.ps = ps || sdataRef.current.ps;
        sdataRef.current.ids = ids || '';
        
        // For analytics, load all data
        const analyticsParams = loadAnalytics ? { ...sdataRef.current, ps: 0 } : sdataRef.current;
        
        util.showLoader();
        TicketInventoryService.lists3(analyticsParams, getAssignedToUsers).then(({ data }) => {
            if (loadAnalytics) {
                setAnalyticsData(data.result.data);
                // After loading analytics data, load paginated data for table
                if (data.result.data.length > 0) {
                    list3(1, 25, false, false);
                }
            } else {
                setResult({ ...data.result });
            }
            
            if (data.assigned_users) {
                setAssignedUsers(data.assigned_users);
            }
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }

    // Load analytics data on component mount for national users
    const loadAnalyticsData = () => {
        if (isNational === 1) {
            list3(1, 0, 'getAssignedToUsers', true);
        } else {
            list3(0, 0, 'getAssignedToUsers');
        }
    };

    useEffect(() => {
        loadAnalyticsData();

        FacilityService.all({ status: 1, include_me: 'Y' }).then(res => {
            let allClients = res.data.result.data.map(v => { return { id: v.id, name: v.business_name, is_national: v.is_national, is_me: v.is_me } });
            setClients(allClients);
        });

        AssetService.infrastuctureSubCats({ status: 1 }).then(res => {
            setInfrastuctureSubCats(res.data.result);
        });

        AssetService.lists({ cat_type: 'Med/NonMed', my_only: 'Y' }).then(res => {
            setAssets([...res.data.result.data.map((v) => { return { id: v.id, 
                            name: ((v.serial_no ? (v.serial_no + ' - ') : '')
                            + v.item_full_name) + ' [' + v.cat + ']' } })]);});

        FacilityService.allStationPoints({ status: 1 }).then(res => {
            setStationPoints([...res.data.result.data]);
        });

        return () => { message.destroy() }
    }, []);

    const downloadPdf=()=>{
        UnallocatedReoprt.generateStockTransactionsReport(sdataRef,util.Orientation.LANSCAPE);  
    }
    
    const download =() => {
        sdataRef.current.p=1;
        sdataRef.current.ps=0;
        util.showLoader();
        TicketInventoryService.lists3(sdataRef.current).then(({data})=>{
            let header = {
                client:'FACILITY',
                ticket_no:'TICKET NUMBER',
                type: 'TYPE',
                item_full_name: 'ITEM',
                created_by_name: 'REQUEST BY',
                created: 'DATE CREATED',
                days_open:'NUMBER OF DAYS OPEN',
                status_name: 'TICKET STATUS',
            };

            let blob=util.convertToCSVBlob(data.result.data, header);
            saveAs(blob, "unallocated-tickets-report.csv");

        }).catch(e=>{
            message.error(e.message);
        }).finally(()=>{
            util.hideLoader();
        })
    }

    const handleSearch = () => {
        list3();
        // Reload analytics data after search for national users
        if (isNational === 1) {
            setTimeout(() => {
                list3(1, 0, false, true);
            }, 500);
        }
    };

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Un Allocated Job Cards</h2>
                </div>
                {isNational === 1 && (
                    <div className="my-auto ml-auto">
                        <Button 
                            type={showAnalytics ? "primary" : "default"}
                            onClick={() => setShowAnalytics(!showAnalytics)}
                            size="small"
                        >
                            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
                        </Button>
                    </div>
                )}
            </div>

            <div className="page-pad">
                {/* Analytics Section - Load by default for national users */}
                {showAnalytics && isNational === 1 && (
                    <TicketAnalytics data={analyticsData} isNational={isNational} />
                )}

                {result.data.length > 0 &&
                    <div className="text-secondary mb8">
                        Showing {result.page.start + 1} - {result.page.start + result.page.total} of {result.page.total_records} records.
                    </div>
                }
                
                <div className="d-flex tbl-search-head">
                    <div className="my-auto flex-grow-1 pr10">
                        <SearchForm 
                            dataRef={sdataRef} 
                            onSearch={handleSearch} 
                            clients={clients}
                            isNational={isNational}
                            assignedUsers={assignedUsers}
                        />
                    </div>
                    <div className="my-auto d-flex" style={{ display: 'flex', alignItems: 'center' }}>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={download} 
                            style={{
                                backgroundColor: 'green', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={excel} alt="download" height={15} /> Export Excel
                        </button>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={downloadPdf} 
                            style={{
                                backgroundColor: 'red', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={acrobat} alt="download" height={15} /> Export PDF
                        </button>
                    </div>
                </div>

                <If cond={result.data.length}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm font-xs table-striped table-hover m-0">
                            <thead className="thead-light text-uppercase table-text-vmid pad-y-md">
                                <tr>
                                    <th className="w20">SN</th>
                                    {isNational === 1 && <th className="w100">Facility</th>}
                                    <th className="w80 nowrap">Job Card No</th>
                                    <th className="w100">Type</th>
                                    <th>Subject</th>
                                    <th className="w100">Created On</th>
                                    <th className="w100">Days Been Open</th>
                                    <th className="w80">Status</th>
                                </tr>
                            </thead>
                            <tbody className="table-text-top font-sm">
                                {result.data.map((v, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}.</td>
                                        {isNational === 1 && <td>{v.client}</td>}
                                        <td>
                                            <div className="ticket-btn">
                                                {v.ticket_no}
                                            </div>
                                        </td>
                                        <td>{v.type}</td>
                                        <td>
                                            <div className="uc bold600">{v.subject}</div>
                                            <div className="pt2 text-secondary fs11">
                                                {v.type === 'Med/NonMed' ? (
                                                    <div>
                                                        {v.serial_no ? v.serial_no + ' - ' : ''} {v.item_full_name} [{v.cat}]
                                                    </div>
                                                ) : (
                                                    <div>Infrastructure Type: <span className="bold600">{v.infrastucture_type}</span></div>
                                                )}
                                            </div>
                                            <div className="pt3 text-secondary fs11">
                                                Created By: <span className="bold600">{v.created_by_name} [{v.created_by_role}]</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="nowrap">{util.getDate(v.created, 'DD MMM YYYY @ hh:mm A')}</div>
                                        </td>
                                        <td className="nowrap uc">
                                            <span style={{
                                                color: parseInt(v.days_open) > 30 ? '#ff4d4f' : 
                                                       parseInt(v.days_open) > 15 ? '#faad14' : '#52c41a'
                                            }}>
                                                {v.days_open}
                                                {parseInt(v.days_open) > 30 && <span style={{marginLeft: '4px'}}>⚠️</span>}
                                            </span>
                                        </td>
                                        <td className="nowrap uc">
                                            <Badge 
                                                color={
                                                    v.status_name === 'Open' ? '#ff4d4f' :
                                                    v.status_name.includes('On Hold') ? '#faad14' :
                                                    v.status_name === 'Completed' ? '#52c41a' : '#722ed1'
                                                }
                                                text={v.status_name}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex tbl-foot-bx">
                        <AntdPaging
                            onChange={list3}
                            total={result.page.total_records}
                            current={result.page.cur_page}
                            pageSize={sdataRef.current.ps}
                            showSizeChanger
                        />
                    </div>
                </If>
                <If cond={!result.data.length}>
                    <div className="no-rec">No record found</div>
                </If>
            </div>
        </div>
    )
}

const SearchForm = (props) => {
    let { dataRef, onSearch, clients, ticketStatusNames, isNational, assignedUsers } = props;
    let [data, setData] = useState({ ...dataRef.current });

    const handleChange = (v, k) => {
        data[k] = v;
        if (k === 'client_id') {
            data.assigned_to = null;
        }
        setData({ ...data });
    }
    
    useEffect(() => {
        dataRef.current = { ...data };
    }, [data]);

    useEffect(() => {
        let ob = clients.find(v => v.is_me);
        setData({ ...data, client_id: ob?.id });
    }, [clients]);

    useEffect(() => {
        setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
    }, [dataRef.current.p, dataRef.current.ps]);

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                {isNational === 1 &&
                    <div className="w300 mr5">
                        <div className="text-secondary fs11 mb2">Facility</div>
                        <AntdSelect
                            placeholder="Facility (All)"
                            showSearch
                            options={clients}
                            value={data.client_id}
                            onChange={v => handleChange(v, 'client_id')}
                        />
                    </div>
                }

                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">Type</div>
                    <AntdSelect
                        placeholder="Type"
                        allowClear
                        options={[{ value: 'Med/NonMed', label: 'Med/NonMed' }, { value: 'Infrastructure', label: 'Infrastructure' }]}
                        value={data.type}
                        onChange={v => handleChange(v, 'type')}
                    />
                </div>

                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">From Date</div>
                    <AntdDatepicker
                        placeholder="From Date"
                        allowClear
                        value={data.from_date}
                        onChange={v => {
                            if (data.to_date && moment(v) > moment(data.to_date)) {
                                message.error("From date should be less than or equal to To Date");
                                v = null;
                            }
                            handleChange(v, 'from_date');
                        }}
                    />
                </div>

                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">To Date</div>
                    <AntdDatepicker
                        placeholder="To Date"
                        allowClear
                        value={data.to_date}
                        onChange={v => {
                            if (data.from_date && moment(v) < moment(data.from_date)) {
                                message.error("To date should be greater than or equal to From Date");
                                v = null;
                            }
                            handleChange(v, 'to_date');
                        }}
                    />
                </div>

                <div className="w160 mr5">
                    <div className="text-secondary fs11 mb2">Job Card#</div>
                    <Input placeholder="Job Card Number" allowClear value={data.k} onChange={e => handleChange(e.target.value, 'k')} />
                </div>

                <div className="pt15">
                    <Button className="mt3" type="primary" onClick={() => onSearch()}><img src={search} alt='' height={15}/></Button>
                </div>
            </div>
        </form>
    )
}